import { useState, type ReactNode } from 'react';
import {
    NJIconButton,
    NJSidebarBrandDeprecated,
    NJSidebarCloseDeprecated,
    NJSidebarDeprecated,
    NJSidebarDividerDeprecated,
    NJSidebarMenuDeprecated,
} from '@engie-group/fluid-design-system-react';
import { EngieFooterLogo, SIZE2MIX_BRAND_FOLDED_IMAGE_SRC, SIZE2MIX_BRAND_IMAGE_SRC } from './brand';
import type { Size2MixPrimaryNavId } from './types';
import './reference.css';
import latoRegular from './assets/lato-regular.ttf?url';
import latoBold from './assets/lato-bold.ttf?url';
import materialIconsOutlined from './assets/material-icons-outlined.otf?url';

/** Height taken by the PrototypeStudioLayout chrome (48px studio header + 28px footer). */
export const PROTOTYPE_CHROME_HEIGHT = 76;

const PRIMARY_SIDEBAR_WIDTH_OPEN = 230;
const PRIMARY_SIDEBAR_WIDTH_FOLDED = 72;

const PRIMARY_NAV: Array<{ id: Size2MixPrimaryNavId; icon: string; label: string; ai?: boolean }> = [
    { id: 'networks', icon: 'account_tree', label: 'Réseaux' },
    { id: 'contracts', icon: 'account_tree', label: 'Contrats' },
    { id: 'calculations', icon: 'legend_toggle', label: 'Calculs' },
    { id: 'coreai', icon: 'engie_ai', label: 'Nemo CoreAI', ai: true },
];

const PRIMARY_FOOTER: Array<{ id: string; icon: string; label: string }> = [
    { id: 'user-guide', icon: 'help_outline', label: 'Guide utilisateur' },
    { id: 'profile', icon: 'account_circle', label: 'Profil' },
    { id: 'disconnect', icon: 'power_settings_new', label: 'Déconnexion' },
];

const SHELL_CSS = `
  @font-face { font-family:Lato; font-style:normal; font-weight:400; font-display:swap; src:url("${latoRegular}") format("truetype"); }
  @font-face { font-family:Lato; font-style:normal; font-weight:700; font-display:swap; src:url("${latoBold}") format("truetype"); }
  @font-face { font-family:"Material Icons Outlined"; font-style:normal; font-weight:400; src:url("${materialIconsOutlined}") format("opentype"); }
  [data-size2mix-shell] { --size2mix-reference-blue: #007acd; }
  [data-size2mix-shell] .material-icons { font-family: "Material Icons Outlined"; }
  .nemo-secondary-nav .nj-list-deprecated__item > button { display:flex; width:100%; gap:16px; }
  .nemo-secondary-nav .nj-list-deprecated__item--active .nj-icon-material { color: var(--size2mix-reference-blue); }
  .nemo-primary-sidebar {
    position: relative !important; top: 0 !important; left: 0 !important;
    width: 100% !important; height: 100% !important; min-width: 0 !important;
  }
  .nemo-primary-sidebar .size2mix-hidden { display: none !important; }
  .nemo-primary-sidebar .nj-list-deprecated__item--clickable,
  .nemo-primary-sidebar .nj-list-deprecated__item--active {
    border-right: none !important;
    box-shadow: none !important;
  }
  .nemo-primary-sidebar .nj-list-deprecated__item--active {
    background-color: var(--nj-core-color-blue-alpha-100) !important;
  }
  .nemo-primary-sidebar .nj-list-deprecated__item--clickable:hover {
    box-shadow: inset -3px 0 0 var(--nj-semantic-color-border-brand-primary-default) !important;
  }
  .nemo-primary-sidebar .nj-list-deprecated__item button {
    display: flex !important; align-items: center !important;
    justify-content: center !important; width: 100% !important; gap: 16px;
  }
  .nemo-secondary-nav .nj-list-deprecated__item--clickable:hover { background-color: var(--nj-core-color-blue-alpha-200); }
  .nemo-secondary-nav .nj-list-deprecated__item--clickable > button { font: inherit; text-align: left; }
`;

export interface Size2MixAppShellProps {
    /** Primary sidebar entry highlighted as active. */
    activePrimary?: Size2MixPrimaryNavId;
    /** Called when a primary entry is clicked (wire it to `useSize2MixRefNavigation` to jump between ref pages). */
    onPrimaryNavigate?: (id: Size2MixPrimaryNavId) => void;
    /** Optional secondary navigation column (e.g. network pages, CoreAI conversations). */
    secondaryNav?: ReactNode;
    /** Padding of the main scroll area (Nemo `p-5` = 20px). Use 0 for full-bleed content (tabs, maps, editors). */
    contentPadding?: number | string;
    children: ReactNode;
}

/**
 * Nemo application shell — primary sidebar + header + footer, sized to fit inside PrototypeStudioLayout.
 * Replaces the copy-pasted Pattern 4a / 4b shells.
 */
export function Size2MixAppShell({
    activePrimary = 'networks',
    onPrimaryNavigate,
    secondaryNav,
    contentPadding = 20,
    children,
}: Size2MixAppShellProps) {
    const [isFolded, setIsFolded] = useState(true);

    const renderItem = (id: string, label: string, icon: ReactNode, isActive: boolean, onClick?: () => void) => (
        <li
            key={id}
            className={`nj-list-deprecated__item nj-list-deprecated__item--clickable${isActive ? ' nj-list-deprecated__item--active' : ''}`}
            style={{ overflow: 'hidden' }}
        >
            <button type="button" aria-label={label} title={label} style={{ overflow: 'hidden' }} onClick={onClick}>
                {icon}
                <div className={`nj-list-deprecated__item-content${isFolded ? ' size2mix-hidden' : ''}`} style={{ color: 'var(--nj-core-color-grey-600)' }}>
                    {label}
                </div>
            </button>
        </li>
    );

    const materialIcon = (name: string, active = false) => (
        <span
            className={`material-icons nj-icon-material nj-icon-material--md nj-list-deprecated__item-icon ${active ? 'nj-icon-material--brand' : 'nj-icon-material--grey'}`}
            aria-hidden="true"
        >
            {name}
        </span>
    );

    return (
        <div
            data-size2mix-shell
            style={{
                display: 'flex',
                height: `calc(100vh - ${PROTOTYPE_CHROME_HEIGHT}px)`,
                minHeight: 560,
                overflow: 'hidden',
                fontFamily: 'var(--nj-semantic-font-family-default, Lato, sans-serif)',
            }}
        >
            <style>{SHELL_CSS}</style>

            <div
                style={{
                    width: isFolded ? PRIMARY_SIDEBAR_WIDTH_FOLDED : PRIMARY_SIDEBAR_WIDTH_OPEN,
                    flexShrink: 0,
                    height: '100%',
                    overflow: 'hidden',
                    transition: 'width 180ms ease',
                }}
            >
                <NJSidebarDeprecated isFolded={isFolded} className="nemo-primary-sidebar">
                    <NJSidebarBrandDeprecated imageSrc={SIZE2MIX_BRAND_IMAGE_SRC} imageFoldedSrc={SIZE2MIX_BRAND_FOLDED_IMAGE_SRC} />
                    <NJSidebarMenuDeprecated isFolded={isFolded}>
                        <>
                            {PRIMARY_NAV.map((item) =>
                                renderItem(
                                    item.id,
                                    item.label,
                                    item.ai ? (
                                        <span className="nj-list-deprecated__item-icon">
                                            <span className="nj-icon-material nj-icon-material--md nj-icon-material--ai nj-icon-material--engie" aria-hidden="true">
                                                engie_ai
                                            </span>
                                        </span>
                                    ) : (
                                        materialIcon(item.icon, item.id === activePrimary)
                                    ),
                                    item.id === activePrimary,
                                    () => onPrimaryNavigate?.(item.id),
                                ),
                            )}
                        </>
                    </NJSidebarMenuDeprecated>
                    <NJSidebarMenuDeprecated isFolded={isFolded} isFooterNav>
                        <>
                            {renderItem('legacy', 'Ancienne Version', materialIcon('history'), false)}
                            <NJSidebarDividerDeprecated />
                            {PRIMARY_FOOTER.map((item) => renderItem(item.id, item.label, materialIcon(item.icon), false))}
                        </>
                    </NJSidebarMenuDeprecated>
                    <NJSidebarCloseDeprecated onClick={() => setIsFolded((f) => !f)}>Réduire</NJSidebarCloseDeprecated>
                </NJSidebarDeprecated>
            </div>

            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minWidth: 0,
                    background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
                }}
            >
                <Size2MixHeader />
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                    {secondaryNav}
                    <main
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: contentPadding,
                            minWidth: 0,
                            borderLeft: secondaryNav ? '1px solid var(--nj-core-color-grey-300)' : undefined,
                        }}
                    >
                        {children}
                    </main>
                </div>
                <Size2MixFooter />
            </div>
        </div>
    );
}

export function Size2MixHeader() {
    return (
        <header
            style={{
                height: 48,
                borderBottom: '1px solid var(--nj-core-color-grey-300)',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                flexShrink: 0,
            }}
        >
            <div
                style={{
                    fontSize: 12,
                    padding: '8px 12px 0',
                    background: 'var(--nj-semantic-color-background-neutral-tertiary-translucent-default)',
                    alignSelf: 'stretch',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <span style={{ lineHeight: '15px' }}>Study</span>
            </div>
            <div style={{ margin: '0 16px', borderLeft: '1px dashed var(--nj-core-color-grey-300)', alignSelf: 'stretch' }} />
            <div style={{ display: 'flex', marginRight: 16, alignItems: 'center' }}>
                <NJIconButton icon="search" label="" aria-label="search action" variant="tertiary" scale="lg" />
                <NJIconButton icon="more_time" label="" aria-label="more time action" variant="tertiary" scale="lg" />
                <button type="button" aria-label="Langue française" style={{ width: 30.984375, height: 40, padding: 0, border: 0, fontFamily: 'system-ui', fontSize: 16, color: 'var(--nj-semantic-color-text-neutral-tertiary-default)', background: 'var(--nj-semantic-color-background-neutral-primary-default)', marginLeft: 12 }}>FR</button>
                <NJIconButton icon="keyboard_arrow_down" label="" aria-label="IconButton" scale="md" />
            </div>
        </header>
    );
}

const FOOTER_LINK_STYLE = { fontSize: 12, lineHeight: '18px', padding: '4px 0' } as const;

export function Size2MixFooter() {
    return (
        <footer
            role="contentinfo"
            style={{
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
                borderTop: '1px solid var(--nj-core-color-grey-300)',
            }}
        >
            <div id="engie-logo" style={{ flex: '1 1 25%', maxWidth: '16.6667%', padding: '0 16px' }}>
                <EngieFooterLogo />
            </div>
            <div id="app-version" style={{ flex: '1 1 25%' }} />
            <div id="links" style={{ marginRight: 80, flex: '1 1 1%', display: 'flex', justifyContent: 'space-around', width: '100%', fontSize: 12, lineHeight: '18px' }}>
                <div>
                    <a href="https://walnut-engie.atlassian.net/servicedesk/customer/portal/10" target="_blank" rel="noreferrer" className="nj-link nj-link--contextual" style={FOOTER_LINK_STYLE}>
                        Contacter le support
                    </a>
                </div>
                <div>
                    <a className="nj-link nj-link--contextual" style={FOOTER_LINK_STYLE} href="#" onClick={(e) => e.preventDefault()}>
                        Politique de confidentialité
                    </a>
                </div>
                <div>
                    <a className="nj-link nj-link--contextual" style={FOOTER_LINK_STYLE} href="#" onClick={(e) => e.preventDefault()}>
                        Politique de cookies
                    </a>
                </div>
            </div>
        </footer>
    );
}
